// src/pages/SolutionsPage.jsx

import React, { useEffect, useRef, useState } from "react";
import { http } from "../../axios/axios";
import SolutionCard from "./SolutionCard";
import { Box } from "lucide-react";
import Pagination from "../Pagination";

const ITEMS_PER_PAGE = 12;

const SolutionsPage = () => {
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const cardsSectionRef = useRef(null);

  const totalPages = Math.ceil(solutions.length / ITEMS_PER_PAGE);

  useEffect(() => {
    const fetchSolutions = async () => {
      try {
        const res = await http.get("/solutions");
        const data = res.data?.data || [];
        const dataTotalPages = Math.ceil(data.length / ITEMS_PER_PAGE);

        setSolutions(data);
        setCurrentPage((page) => (page > dataTotalPages ? 1 : page));
      } catch (err) {
        console.error(
          "Error fetching solutions:",
          err.response?.data || err.message
        );

        setSolutions([]);
        setCurrentPage(1);
      } finally {
        setLoading(false);
      }
    };

    fetchSolutions();
  }, []);

  const displayedPage = currentPage > totalPages ? 1 : currentPage;
  const displayedSolutions = solutions.slice(
    (displayedPage - 1) * ITEMS_PER_PAGE,
    displayedPage * ITEMS_PER_PAGE
  );

  const changePage = (page) => {
    if (page < 1 || page > totalPages || page === displayedPage) return;

    setCurrentPage(page);
    cardsSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="min-h-screen bg-white">
      {/* Page Header */}
      <section className="px-4 sm:px-6 lg:px-8 pt-10 pb-6 text-center">
        <p className="text-xs sm:text-sm font-bold uppercase tracking-widest text-blue-600">
          Our Expertise
        </p>

        <h1 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">
          Security & IT Solutions
        </h1>

        <p className="mt-3 max-w-3xl mx-auto text-sm sm:text-base text-slate-500 leading-relaxed">
          Explore our advanced security surveillance and IT solutions designed
          for homes, offices, societies, hospitals, schools, industries and
          commercial environments.
        </p>
      </section>

      {/* Solutions */}
      <section
        ref={cardsSectionRef}
        className="px-4 sm:px-6 lg:px-8 pb-12"
      >
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-700" />
          </div>
        ) : solutions.length > 0 ? (
          <>
            <div
              className="
                grid
                grid-cols-1
                sm:grid-cols-2
                lg:grid-cols-4
                gap-6
                max-w-7xl
                mx-auto
              "
            >
              {displayedSolutions.map((solution, index) => (
                <SolutionCard
                  key={solution._id || index}
                  solution={solution}
                />
              ))}
            </div>
            <Pagination
              page={displayedPage}
              totalPages={totalPages}
              onPageChange={changePage}
            />
          </>
        ) : (
          <div
            className="
              max-w-7xl
              mx-auto
              py-20
              text-center
              border
              border-dashed
              border-slate-300
              rounded-2xl
              bg-slate-50
            "
          >
            <Box
              size={40}
              className="mx-auto text-slate-300 mb-4"
            />

            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              No Solutions Available
            </h3>

            <p className="text-sm text-slate-500">
              We are currently updating our solutions. Please check back soon.
            </p>
          </div>
        )}
      </section>
    </main>
  );
};

export default SolutionsPage;